import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import Alert from '@mui/material/Alert';
import { alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import ContentCopy from '@mui/icons-material/ContentCopy';
import axios from '../../../../api/axios';
import { ERROR_SNACKBAR, PORTAL_SETTINGS_ENDPOINT, SUCCESS_SNACKBAR } from '../../../../consts';
import { useHasPermission } from '../../../../contexts/auth';
import {
  CLIENT_PORTAL_MODULE,
  ClientPortalModuleValue,
  ClientPortalSettings,
} from '../../../../types/portal';
import { DetailPage } from '../../../../components/ui/detailView';

// Display order for the multi-select. Also the order the value is saved in, so
// what gets stored never depends on the order the options were clicked.
const MODULE_OPTIONS: { value: ClientPortalModuleValue; labelKey: string }[] = [
  { value: CLIENT_PORTAL_MODULE.TECH_SUPPORT, labelKey: 'Technical support' },
  { value: CLIENT_PORTAL_MODULE.COUNTER_READINGS, labelKey: 'Counter readings' },
];

const sortModules = (modules: ClientPortalModuleValue[]): ClientPortalModuleValue[] =>
  MODULE_OPTIONS.map((option) => option.value).filter((value) => modules.includes(value));

const sameModules = (left: ClientPortalModuleValue[], right: ClientPortalModuleValue[]) =>
  left.length === right.length && left.every((value, index) => value === right[index]);

export const TenantClientPortal = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const canChange = useHasPermission('config.change_tenantsettings');

  // `settings` is what the server last confirmed; `enabled`/`modules` are the
  // draft the user is editing. Nothing is sent until Save is pressed.
  const [settings, setSettings] = useState<ClientPortalSettings | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [modules, setModules] = useState<ClientPortalModuleValue[]>([]);
  const [saving, setSaving] = useState(false);

  const applySettings = useCallback((data: ClientPortalSettings) => {
    setSettings(data);
    setEnabled(data.client_portal_enabled);
    setModules(sortModules(data.client_portal_modules));
  }, []);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await axios.get(PORTAL_SETTINGS_ENDPOINT);
        applySettings(response.data);
      } catch {
        enqueueSnackbar(t('Could not load the client portal settings.'), ERROR_SNACKBAR);
      }
    };

    load();
  }, [applySettings, enqueueSnackbar, t]);

  const hasChanges = useMemo(() => {
    if (!settings) return false;

    return (
      settings.client_portal_enabled !== enabled ||
      !sameModules(sortModules(settings.client_portal_modules), modules)
    );
  }, [settings, enabled, modules]);

  const handleModulesChange = (event: SelectChangeEvent<ClientPortalModuleValue[]>) => {
    const value = event.target.value;
    setModules(
      sortModules((typeof value === 'string' ? value.split(',') : value) as ClientPortalModuleValue[])
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.patch(PORTAL_SETTINGS_ENDPOINT, {
        client_portal_enabled: enabled,
        client_portal_modules: modules,
      });
      applySettings(response.data);
      enqueueSnackbar(t('Client portal settings saved.'), SUCCESS_SNACKBAR);
    } catch {
      enqueueSnackbar(t('Could not save the client portal settings.'), ERROR_SNACKBAR);
    } finally {
      setSaving(false);
    }
  };

  const copyPortalUrl = async () => {
    if (!settings?.portal_url) return;

    await navigator.clipboard.writeText(settings.portal_url);
    enqueueSnackbar(t('Portal link copied.'), SUCCESS_SNACKBAR);
  };

  // A portal ticket becomes a job, and a job needs a number - without the
  // default job numeration schema the module is switched on but unusable.
  const ticketsNeedNumeration =
    enabled &&
    modules.includes(CLIENT_PORTAL_MODULE.TECH_SUPPORT) &&
    !settings?.has_default_job_numeration_schema;

  if (!settings) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <DetailPage>
      <Typography variant="body2" color="text.secondary">
        {t(
          "Share this link with your clients. They sign in with a one-time code sent to an email address you have granted access to, on the client's Portal access tab."
        )}
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 2 }}>
        <TextField
          label={t('Portal link')}
          value={settings.portal_url || '-'}
          slotProps={{ input: { readOnly: true } }}
          fullWidth
        />
        <Tooltip title={t('Copy link')}>
          <span>
            <IconButton onClick={copyPortalUrl} disabled={!settings.portal_url}>
              <ContentCopy />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      <Divider sx={{ my: 3 }} />

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
          px: 2,
          py: 1.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: (theme) =>
            enabled ? alpha(theme.palette.success.main, 0.4) : theme.palette.divider,
          bgcolor: (theme) =>
            enabled ? alpha(theme.palette.success.main, 0.08) : theme.palette.action.hover,
        }}
      >
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            {t('Client portal')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {enabled
              ? t('Clients can open the portal link and sign in.')
              : t('The portal link is closed to everyone.')}
          </Typography>
        </Box>
        <FormControlLabel
          sx={{ mr: 0 }}
          labelPlacement="start"
          control={
            <Switch
              color="success"
              checked={enabled}
              disabled={!canChange || saving}
              onChange={(event) => setEnabled(event.target.checked)}
            />
          }
          label={
            <Typography
              variant="body2"
              sx={{ fontWeight: 700, color: enabled ? 'success.dark' : 'text.secondary' }}
            >
              {enabled ? t('Enabled') : t('Disabled')}
            </Typography>
          }
        />
      </Box>

      {ticketsNeedNumeration ? (
        <Alert severity="warning" sx={{ mt: -1 }}>
          {t(
            'Technical support is enabled but no default job numeration schema is set, so clients cannot report a fault yet. Set one under Settings, on the Numbering tab.'
          )}
        </Alert>
      ) : null}

      <FormControl fullWidth sx={{ mt: -1 }} disabled={!canChange || saving || !enabled}>
        <InputLabel>{t('Available sections')}</InputLabel>
        <Select
          multiple
          label={t('Available sections')}
          value={modules}
          onChange={handleModulesChange}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {(selected as ClientPortalModuleValue[]).map((value) => (
                <Chip
                  key={value}
                  size="small"
                  color="primary"
                  variant="outlined"
                  label={t(MODULE_OPTIONS.find((o) => o.value === value)?.labelKey ?? value)}
                />
              ))}
            </Box>
          )}
        >
          {MODULE_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {t(option.labelKey)}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      {canChange ? (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
          <Button
            variant="contained"
            color="success"
            disabled={!hasChanges || saving}
            onClick={handleSave}
          >
            {saving ? t('Saving...') : t('Save')}
          </Button>
        </Box>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>
          {t('You do not have permission to change these settings.')}
        </Typography>
      )}
    </DetailPage>
  );
};
